/** Istilah operasional gudang — konsisten di seluruh PWA tablet gudang. */
export const warehouseTerms = {
  appName: 'Superoti Gudang',
  modules: {
    home: 'Beranda',
    attendance: 'Absensi',
    stock: 'Stok',
    dispatches: 'Pengiriman',
  },
  roles: {
    headWarehouse: 'Kepala gudang',
  },
  shell: {
    logout: 'Keluar',
  },
  activation: {
    title: 'Aktivasi tablet gudang',
    subtitle: 'Masukkan kode aktivasi sekali pakai dari Admin HO.',
    codeLabel: 'Kode aktivasi',
    codeHelper: 'Ketik atau tempel 6 karakter dari Admin HO (tanpa strip).',
    footer: 'Belum punya kode? Hubungi Admin HO.',
    cta: 'Aktivasi',
    verifying: 'Memverifikasi…',
    success: 'Perangkat teraktivasi',
    wrongDeviceType: 'Kode ini bukan untuk tablet gudang.',
    activateFailed: 'Aktivasi gagal. Coba lagi.',
  },
  login: {
    title: 'Login PIN',
    subtitle: 'Masukkan PIN 6 digit kepala/staff gudang untuk mulai operasional.',
    helper: 'Isi 6 digit PIN untuk lanjut.',
    submit: 'Masuk',
    submitting: 'Memverifikasi…',
    roleNotAllowed: 'Role tidak diizinkan login di tablet gudang.',
  },
  home: {
    greeting: (name: string) => `Halo, ${name}`,
    greetingFallback: 'Halo',
    subtitle: (warehouseName: string, dateLabel: string) => `${warehouseName} · ${dateLabel}`,
    actionsTitle: 'Perlu tindakan hari ini',
    actionsCount: (count: number) => `${count} aksi`,
    emptyTitle: 'Semua modul sudah rapi',
    emptyDescription: 'Tidak ada draft atau serah yang perlu ditangani hari ini.',
    menuTitle: 'Menu utama',
    refreshLabel: 'Perbarui ringkasan',
    refreshLoading: 'Memperbarui…',
    loadError: 'Gagal memuat ringkasan hari ini.',
    retry: 'Coba lagi',
    loadSummary: 'Memuat ringkasan hari ini',
    priority: {
      high: 'Prioritas',
      medium: 'Perlu dicek',
    },
    tiles: {
      newDispatchTitle: 'Buat pengiriman',
      newDispatchDescription: 'Kirim bahan baku ke dapur via delman.',
      todayDispatchTitle: 'Pengiriman hari ini',
      todayDispatchSubtitle: (draftCount: number, dispatchedCount: number) =>
        `${draftCount} draft · ${dispatchedCount} diserahkan`,
    },
  },
  stock: {
    title: 'Stok bahan baku',
    subtitle: (updatedLabel: string) => `Diperbarui ${updatedLabel}`,
    searchPlaceholder: 'Cari nama atau kode material…',
    searchAria: 'Cari bahan baku',
    refreshAria: 'Muat ulang stok',
    loading: 'Memuat stok…',
    retry: 'Coba lagi',
    loadError: 'Gagal memuat stok bahan baku.',
    summary: {
      overview: (typeCount: number, categoryCount: number) =>
        `${typeCount} jenis · ${categoryCount} kategori`,
    },
    empty: {
      title: 'Belum ada stok',
      description: 'Stok bahan baku akan tampil di sini setelah HO mengisi saldo awal.',
      searchDescription: 'Tidak ada material yang cocok dengan pencarian.',
    },
    packagingLabel: (packaging: string, packSize: number) => `${packaging} · ${packSize}`,
    itemCountLabel: 'bahan',
  },
  dispatchList: {
    title: 'Pengiriman hari ini',
    subtitle: 'Draft, serah, dan status transit ke dapur.',
    refreshAria: 'Muat ulang pengiriman',
    loading: 'Memuat pengiriman…',
    retry: 'Coba lagi',
    loadError: 'Gagal memuat daftar pengiriman.',
    newDispatch: '+ Buat pengiriman baru',
    draftSection: 'Draft',
    sentSection: 'Sudah diserahkan',
    emptyTitle: 'Belum ada pengiriman hari ini',
    emptyDescription: 'Buat pengiriman baru untuk mengirim bahan baku ke dapur.',
    continueEdit: 'Lanjutkan draft →',
    reDispatch: 'Lihat / serah ulang →',
    viewOnly: 'Lihat →',
    lineSummary: (lineCount: number) => `${lineCount} bahan`,
    backHome: 'Kembali ke beranda',
    status: {
      draft: 'Draft',
      dispatched: 'Diserahkan',
      in_transit: 'Dalam transit',
      receiving_draft: 'Diterima dapur (draft)',
      receiving_completed: 'Selesai diterima',
      cancelled: 'Dibatalkan',
    } as Record<string, string>,
  },
  dispatchEditor: {
    titleNew: 'Pengiriman baru',
    titleExisting: (code: string) => code,
    targetKitchen: 'Dapur tujuan',
    delman: 'Delman gudang',
    targetKitchenPlaceholder: 'Pilih dapur tujuan',
    delmanPlaceholder: 'Pilih delman gudang',
    materialSectionTitle: 'Baris material',
    materialSectionHint: 'Isi qty untuk material yang mau dikirim. Qty dibatasi stok gudang saat ini.',
    maxStock: (qty: number, uom: string) => `Stok: ${qty} ${uom}`,
    noStock: 'Stok kosong',
    back: 'Kembali',
    showAll: 'Tampilkan semua',
    hideEmpty: 'Sembunyikan kosong',
    footerFilled: (n: number, qtySummary: string) =>
      qtySummary ? `${n} bahan terisi · ${qtySummary}` : `${n} bahan terisi`,
    saveDraft: 'Simpan draft',
    dispatch: 'Serah ke delman',
    reDispatch: 'Serah ulang',
    readOnlyHint: 'Pengiriman ini sudah diproses delman — tidak bisa diubah lagi.',
    loadError: 'Gagal memuat data picker/stok.',
    retry: 'Coba lagi',
    loading: 'Memuat…',
    errors: {
      targetKitchenRequired: 'Pilih dapur tujuan.',
      delmanRequired: 'Pilih delman gudang.',
      minOneLine: 'Isi qty minimal satu baris material.',
      noKitchens: 'Belum ada dapur aktif. Hubungi Admin HO.',
      noDelmans: 'Belum ada delman gudang aktif. Hubungi Admin HO.',
    },
    toast: {
      draftSaved: 'Draft pengiriman tersimpan.',
      saveFailed: 'Gagal menyimpan draft.',
      dispatched: 'Pengiriman diserahkan ke delman.',
      dispatchFailed: 'Gagal menyerahkan pengiriman.',
    },
    pin: {
      title: 'Konfirmasi serah ke delman',
      description: 'Masukkan PIN kepala gudang untuk menyerahkan pengiriman ini ke delman.',
      confirm: 'Serahkan',
    },
  },
} as const
